import { useEffect, useState, useRef, useMemo } from "react";
import "../styles/gis/gis.scss";
import GISHeader from "../components/gis/gisheader";
import LoadingWidget from "../components/loading";
import { FaLayerGroup, FaTimes, FaMapMarkerAlt, FaMap, FaExpand, FaCompress } from "react-icons/fa";
import myData from "../assets/data/data";
import Map from "ol/Map";
import View from "ol/View";
import Icon from 'ol/style/Icon';
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Overlay from "ol/Overlay";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import MultiPolygon from "ol/geom/MultiPolygon";
import MultiLineString from "ol/geom/MultiLineString";
import { ZoomToExtent, defaults as defaultControls } from "ol/control";
import { fromLonLat } from "ol/proj";
import { XYZ } from "ol/source";
import { Style, Circle as CircleStyle, Fill, Stroke } from "ol/style";
import Legend from "../components/gis/legend";
import MenuTab from "../components/menu_tab";
import northarrow from "../assets/icons/north-arrow.png"
import { locateUser } from "../components/gis/locateUser";
import html2canvas from "html2canvas";
import SearchBar from "../components/searchbar";
import { getBoreholeSVG, getBulkMeterSVG, getColoredMeterSVG, getControlValveSVG, getOfftakeSVG, getTankSVG, getTreatmentWorksSVG, getWeirSVG } from "../assets/icons/legendicons/mlegendicons";
import TabularData from "../components/tabular_data";
import { getLayerSchema, buildClassification, SHOW_ALL_KEY, getShowAllClass } from "../components/gis/attributeAnalyzer";

export default function Infrastructure() {
  const [toggleMenu, setToggleMenu] = useState(false);
  const [map, setMap] = useState(null);
  const [basemapSelected, setBasemapSelected] = useState(0);
  const [basemapsVisible, setBasemapsVisible] = useState(true);
  const [legendCollapsed, setLegendCollapsed] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [layers, setLayers] = useState({
    Wards: { url: "/api/wardsserved", data: [], visibility: true, activeAttribute: null, classFilters: {} },
    SupplyLines: { url: "/api/latestsupplylines", data: [], visibility: true, activeAttribute: null, classFilters: {} },
    Weirs: { url: "/api/latestweirs", data: [], visibility: false, activeAttribute: null, classFilters: {} },
    Boreholes: { url: "/api/latestboreholes", data: [], visibility: true, activeAttribute: null, classFilters: {} },
    Offtakes: { url: "/api/latestofftakes", data: [], visibility: true, activeAttribute: null, classFilters: {} },
    ControlValves: { url: "/api/latestcontrolvalves", data: [], visibility: false, activeAttribute: null, classFilters: {} },
    Bulkmeters: { url: "/api/latestbulkmeters", data: [], visibility: false, activeAttribute: null, classFilters: {} },
    "T-Works": { url: "/api/latesttworks", data: [], visibility: true, activeAttribute: null, classFilters: {} },
    Tanks: { url: "/api/latesttanks", data: [], visibility: true, activeAttribute: null, classFilters: {} },
  });

  const [selectedFeature, setSelectedFeature] = useState({ layerName: null, id: null });
  const [isLoading, setIsLoading] = useState(true);

  const mapElement = useRef();
  const mapWrapperRef = useRef();
  const popupElement = useRef();
  const popupOverlay = useRef();

  const hiddenColumns = [
    "geom", "id", "fid", "collected_", "date_colle", "mappedby", "photo",
    "mapping_da", "meter_imag", "__gid", "uid",
  ];

  const pickDefaultAttribute = (fields, layerName) => {
    // Exact "status" field always wins if the layer has one.
    const statusField = fields.find((f) => f.toLowerCase() === "status");
    if (statusField) return statusField;

    // Wards defaults to subcounty specifically.
    if (layerName === "Wards") {
      const subcountyField = fields.find((f) => f.toLowerCase() === "subcounty");
      if (subcountyField) return subcountyField;
    }

    // Otherwise fall back to anything status-like, then the first available field.
    return fields.find((f) => /status/i.test(f)) || fields[0];
  };

  useEffect(() => {
    const fetchData = async () => {
      const requests = Object.entries(layers).map(([key, layer]) =>
        fetch(layer.url).then((res) => (res.ok ? res.json() : Promise.reject(`Failed: ${key}`)))
      );

      const responses = await Promise.allSettled(requests);
      const updatedLayers = { ...layers };

      Object.keys(layers).forEach((key, i) => {
        if (responses[i].status === "fulfilled") {
          const data = responses[i].value.data;
          const schema = getLayerSchema(data);
          const fields = schema.map((s) => s.field);

          updatedLayers[key] = {
            ...updatedLayers[key],
            data,
            activeAttribute: pickDefaultAttribute(fields, key) || null,
          };
          setIsLoading(false);
        } else {
          console.error(`Error fetching ${key}:`, responses[i].reason);
        }
      });

      setLayers(updatedLayers);
    };

    fetchData();
  }, []);

  const layerSchemas = useMemo(() => {
    const result = {};
    Object.entries(layers).forEach(([name, cfg]) => {
      result[name] = getLayerSchema(cfg.data || []);
    });
    return result;
  }, [layers]);

  const layerClassifications = useMemo(() => {
    const result = {};
    Object.entries(layers).forEach(([name, cfg]) => {
      if (cfg.activeAttribute === SHOW_ALL_KEY) {
        result[name] = [getShowAllClass(name)];
        return;
      }
      const schema = layerSchemas[name] || [];
      const fieldInfo = schema.find((s) => s.field === cfg.activeAttribute);
      result[name] = fieldInfo ? buildClassification(cfg.data, cfg.activeAttribute, fieldInfo) : [];
    });
    return result;
  }, [layers, layerSchemas]);

  const handleAttributeChange = (layerName, field) => {
    setLayers((prev) => ({ ...prev, [layerName]: { ...prev[layerName], activeAttribute: field } }));
  };

  const handleToggleClass = (layerName, attribute, classKey) => {
    setLayers((prev) => {
      const cfg = prev[layerName];
      const currentFilters = cfg.classFilters?.[attribute] || {};
      const currentVal = currentFilters[classKey] !== undefined ? currentFilters[classKey] : true;
      return {
        ...prev,
        [layerName]: {
          ...cfg,
          classFilters: { ...cfg.classFilters, [attribute]: { ...currentFilters, [classKey]: !currentVal } },
        },
      };
    });
  };

  useEffect(() => {
    const basemapLayer = new TileLayer({
      title: "Basemaps",
      source: new XYZ({ url: myData[basemapSelected].url, crossOrigin: "Anonymous" }),
    });

    const initialMap = new Map({
      target: mapElement.current,
      layers: [basemapLayer],
      view: new View({
        projection: "EPSG:3857",
        center: fromLonLat([36.777382, -1.040343]),
        zoom: 12.5,
        maxZoom: 32,
      }),
      controls: defaultControls().extend([
        new ZoomToExtent({
          extent: [...fromLonLat([36.6970, -1.1335]), ...fromLonLat([36.8846, -0.9800])],
        }),
      ]),
    });

    setMap(initialMap);
    return () => initialMap.setTarget(undefined);
  }, []);

  useEffect(() => {
    if (!map) return;
    const basemapLayer = map.getLayers().getArray().find((layer) => layer.get("title") === "Basemaps");
    if (basemapLayer) {
      basemapLayer.setSource(new XYZ({ url: myData[basemapSelected].url, crossOrigin: "Anonymous" }));
      basemapLayer.setVisible(basemapsVisible);
    }
  }, [basemapSelected, basemapsVisible]);

  useEffect(() => {
    if (!map) return;

    popupOverlay.current = new Overlay({
      element: popupElement.current,
      autoPan: true,
      positioning: "bottom-center",
      offset: [0, -15],
    });

    map.addOverlay(popupOverlay.current);

    map.on("singleclick", (event) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (feat) => feat);

      if (feature) {
        const properties = feature.getProperties();
        const filteredEntries = Object.entries(properties).filter(
          ([key]) => key !== "geometry" && !hiddenColumns.includes(key)
        );

        const contentHTML = `
          <div class="popup-content-wrapper">
            <table class="popup-table">
              ${filteredEntries
                .map(
                  ([key, value]) =>
                    `<tr><td class="popup-key">${key}</td><td class="popup-value">${
                      typeof value === "object" ? JSON.stringify(value) : value ?? ""
                    }</td></tr>`
                )
                .join("")}
            </table>
            <button class="popup-zoom" id="zoomToFeatureBtn">Zoom to Feature</button>
          </div>
        `;

        document.getElementById("popup-content").innerHTML = contentHTML;
        popupOverlay.current.setPosition(event.coordinate);

        setTimeout(() => {
          const btn = document.getElementById("zoomToFeatureBtn");
          if (btn) {
            btn.onclick = () => {
              const geometry = feature.getGeometry();
              map.getView().fit(geometry.getExtent(), { maxZoom: 17, duration: 800 });
            };
          }
        }, 100);
      } else {
        popupOverlay.current.setPosition(undefined);
      }
    });
  }, [map]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => map?.updateSize(), 150);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [map]);

  const toggleFullscreen = () => {
    const el = mapWrapperRef.current;
    if (!document.fullscreenElement) {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    } else {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    }
  };

  useEffect(() => {
    if (!map) return;

    const svgGenerators = {
      "Tanks": getTankSVG,
      "ControlValves": getControlValveSVG,
      "Boreholes": getBoreholeSVG,
      "Weirs": getWeirSVG,
      "T-Works": getTreatmentWorksSVG,
      "Offtakes": getOfftakeSVG,
      "Bulkmeters": getBulkMeterSVG,
    };

    Object.entries(layers).forEach(([layerName, layerConfig]) => {
      let vectorLayer = map.getLayers().getArray().find((l) => l.get("title") === layerName);

      if (!vectorLayer) {
        vectorLayer = new VectorLayer({ title: layerName, source: new VectorSource() });
        map.addLayer(vectorLayer);
      }

      const source = vectorLayer.getSource();
      source.clear();

      const activeAttribute = layerConfig.activeAttribute;
      const classes = layerClassifications[layerName] || [];
      const classFilters = layerConfig.classFilters?.[activeAttribute] || {};
      const isClassVisible = (key) => (classFilters[key] !== undefined ? classFilters[key] : true);

      if (layerConfig.visibility && layerConfig.data && activeAttribute) {
        const styleCache = {};
        const iconGenerator = svgGenerators[layerName];

        vectorLayer.setStyle((feature) => {
          const featureId = feature.get("featureId");
          const featureLayer = feature.get("layerName");
          const classKey = feature.get("classKey");

          const isSelected =
            selectedFeature && featureId === selectedFeature.id && featureLayer === selectedFeature.layerName;

          const baseStyle = styleCache[classKey];
          if (!isSelected) return baseStyle;

          const geomType = feature.getGeometry().getType();

          if (geomType === "Point") {
            return [
              baseStyle,
              new Style({
                image: new CircleStyle({ radius: 12, fill: new Fill({ color: "rgba(255,165,0,0.3)" }) }),
                zIndex: 999,
              }),
            ];
          }
          if (geomType.includes("LineString")) {
            return new Style({ stroke: new Stroke({ color: "orange", width: 4 }), zIndex: 999 });
          }
          if (geomType.includes("Polygon")) {
            return new Style({
              stroke: new Stroke({ color: "orange", width: 2 }),
              fill: new Fill({ color: "rgba(255,165,0,0.3)" }),
              zIndex: 999,
            });
          }
          return baseStyle;
        });

        const features = layerConfig.data
          .filter((item) => item.geom)
          .map((item) => {
            const matchedClass = classes.find((c) => c.test(item[activeAttribute]));
            if (!matchedClass || !isClassVisible(matchedClass.key)) return null;

            let geometry;
            if (item.geom.type === "Point") {
              geometry = new Point(fromLonLat(item.geom.coordinates));
            } else if (item.geom.type === "MultiLineString") {
              geometry = new MultiLineString(
                item.geom.coordinates.map((line) => line.map(([lon, lat]) => fromLonLat([lon, lat])))
              );
            } else if (item.geom.type === "MultiPolygon") {
              geometry = new MultiPolygon(
                item.geom.coordinates.map((polygon) =>
                  polygon.map((ring) => ring.map(([lon, lat]) => fromLonLat([lon, lat])))
                )
              );
            }

            if (!geometry) return null;

            const feature = new Feature({ geometry, ...item });
            const id = item.id || item.fid || item.gid;

            feature.set("featureId", id);
            feature.set("layerName", layerName);
            feature.set("classKey", matchedClass.key);

            if (!styleCache[matchedClass.key]) {
              if (geometry.getType() === "Point") {
                if (iconGenerator) {
                  styleCache[matchedClass.key] = new Style({
                    image: new Icon({
                      src: iconGenerator(matchedClass.color),
                      anchor: [0.5, 0.5],
                      scale: 1.0,
                      crossOrigin: "anonymous",
                    }),
                  });
                } else {
                  styleCache[matchedClass.key] = new Style({
                    image: new CircleStyle({
                      radius: 6,
                      fill: new Fill({ color: `${matchedClass.color}` }),
                      stroke: new Stroke({ color: "#fff", width: 1 }),
                    }),
                  });
                }
              } else if (geometry.getType().includes("LineString")) {
                styleCache[matchedClass.key] = new Style({
                  stroke: new Stroke({ color: matchedClass.color, width: 1.5 }),
                });
              } else if (geometry.getType().includes("Polygon")) {
                styleCache[matchedClass.key] = new Style({
                  stroke: new Stroke({ color: matchedClass.color, width: 0.9 }),
                  fill: new Fill({ color: `${matchedClass.color}33` }),
                });
              }
            }

            return feature;
          });

        source.addFeatures(features.filter(Boolean));
      }

      vectorLayer.setVisible(layerConfig.visibility);
    });
  }, [map, layers, selectedFeature, layerClassifications]);

  const exportMap = () => {
    if (!map) return;
    const title = prompt("Enter a title for the map:", "My Map Title");
    if (title === null) return;

    map.once("rendercomplete", () => {
      const mapCanvas = map.getViewport().querySelector("canvas");
      if (!mapCanvas) return;

      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = mapCanvas.width;
      exportCanvas.height = mapCanvas.height;
      const ctx = exportCanvas.getContext("2d");
      ctx.drawImage(mapCanvas, 0, 0);

      const legend = document.querySelector(".legend");
      const northArrow = new Image();
      northArrow.src = northarrow;

      northArrow.onload = () => {
        ctx.drawImage(northArrow, exportCanvas.width - 250, 160, 245, 260);

        ctx.fillStyle = "black";
        ctx.fillRect(exportCanvas.width - 180, exportCanvas.height - 60, 100, 6);

        ctx.fillStyle = "white";
        ctx.font = "14px sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.fillText("100m", exportCanvas.width - 170, exportCanvas.height - 65);

        const titleFontSize = 50;
        ctx.font = `bold ${titleFontSize}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";

        const textWidth = ctx.measureText(title).width;
        const textX = exportCanvas.width / 2;
        const textY = 30;
        const padding = 40;

        ctx.fillStyle = "white";
        ctx.fillRect(textX - textWidth / 2 - padding / 2, textY - padding / 2, textWidth + padding, titleFontSize + padding);

        ctx.fillStyle = "black";
        ctx.font = `bold ${titleFontSize}px Arial`;
        ctx.fillText(title, textX, textY);

        ctx.font = "italic 18px Arial";
        ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.fillText("© Githunguri Water and Sanitation Company Ltd.", 20, exportCanvas.height - 20);

        if (legend) {
          html2canvas(legend).then((legendCanvas) => {
            const legendX = exportCanvas.width - legendCanvas.width - 30;
            const legendY = exportCanvas.height - legendCanvas.height - 30;
            ctx.drawImage(legendCanvas, legendX, legendY);
            const link = document.createElement("a");
            link.download = "map_export.png";
            link.href = exportCanvas.toDataURL("image/png");
            link.click();
          });
        } else {
          const link = document.createElement("a");
          link.download = "map_export.png";
          link.href = exportCanvas.toDataURL("image/png");
          link.click();
        }
      };
    });

    map.renderSync();
  };

  const handleRowClick = (row, layerName) => {
    if (!map || !layers[layerName]) return;
    const id = row.id || row.fid || row.gid;
    setSelectedFeature({ layerName, id });

    const item = layers[layerName].data.find((d) => (d.id || d.fid || d.gid) === id);
    if (!item || !item.geom) return;

    let geometry;
    switch (item.geom.type) {
      case "Point":
        geometry = new Point(fromLonLat(item.geom.coordinates));
        break;
      case "MultiLineString":
        geometry = new MultiLineString(item.geom.coordinates.map((line) => line.map(([lon, lat]) => fromLonLat([lon, lat]))));
        break;
      case "MultiPolygon":
        geometry = new MultiPolygon(
          item.geom.coordinates.map((polygon) => polygon.map((ring) => ring.map(([lon, lat]) => fromLonLat([lon, lat]))))
        );
        break;
      default:
        return;
    }

    map.getView().fit(geometry.getExtent(), { padding: [40, 200, 60, 60], duration: 600, maxZoom: 20 });
  };

  return (
    <div className="gis">
      <GISHeader toggleMenu={toggleMenu} setToggleMenu={setToggleMenu} />
      {toggleMenu && <MenuTab toggleMenu={toggleMenu} setToggleMenu={setToggleMenu} />}

      <div className="map" ref={mapWrapperRef}>
        {isLoading && <LoadingWidget />}

        <div className="map_container" ref={mapElement}>
          <div ref={popupElement} className="ol-popup">
            <button className="ol-popup-closer" onClick={() => popupOverlay.current.setPosition(undefined)}>
              <FaTimes />
            </button>
            <div id="popup-content"></div>
          </div>
        </div>

        <div className="map-buttons">
          {legendCollapsed && (
            <button onClick={() => setLegendCollapsed(false)}>
              <FaLayerGroup />
              <span className="tooltip">Layers</span>
            </button>
          )}
          <button onClick={() => locateUser(map)}>
            <FaMapMarkerAlt />
            <span className="tooltip">Locate Me</span>
          </button>
          <button onClick={() => { setLegendCollapsed(false); exportMap(); }}>
            <FaMap />
            <span className="tooltip">Export Map</span>
          </button>
          <button onClick={toggleFullscreen}>
            {isFullscreen ? <FaCompress /> : <FaExpand />}
            <span className="tooltip">{isFullscreen ? "Exit Fullscreen" : "Fullscreen"}</span>
          </button>
        </div>

        <TabularData layers={layers} setLayers={setLayers} handleRowClick={handleRowClick} />
        <SearchBar layers={layers} setLayers={setLayers} />

        {!legendCollapsed && (
          <Legend
            layers={layers}
            setLayers={setLayers}
            layerSchemas={layerSchemas}
            layerClassifications={layerClassifications}
            onAttributeChange={handleAttributeChange}
            onToggleClass={handleToggleClass}
            myData={myData}
            legendCollapsed={legendCollapsed}
            setLegendCollapsed={setLegendCollapsed}
            basemapsVisible={basemapsVisible}
            setBasemapsVisible={setBasemapsVisible}
            basemapSelected={basemapSelected}
            setBasemapSelected={setBasemapSelected}
          />
        )}
      </div>
    </div>
  );
}